import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import {
    FiBriefcase,
    FiMapPin,
    FiClock,
    FiDollarSign,
    FiChevronDown,
    FiChevronUp,
    FiAward,
    FiUsers,
    FiTrendingUp,
    FiX,
    FiShield,
    FiFileText,
    FiCheckCircle,
    FiMail
} from 'react-icons/fi';
import { toast } from 'react-hot-toast';
import SectionHeading from '../components/SectionHeading';
import SEO from '../components/SEO';

const Careers = () => {
    const { t, i18n } = useTranslation();
    const isArabic = i18n.language ? i18n.language.startsWith('ar') : false;

    const benefits = [
        { icon: FiUsers, title: t('careers.benefits.items.environment.title'), description: t('careers.benefits.items.environment.desc') },
        { icon: FiAward, title: t('careers.benefits.items.training.title'), description: t('careers.benefits.items.training.desc') },
        { icon: FiTrendingUp, title: t('careers.benefits.items.growth.title'), description: t('careers.benefits.items.growth.desc') },
        { icon: FiShield, title: t('careers.benefits.items.safety.title'), description: t('careers.benefits.items.safety.desc') },
    ];

    const recruitmentSteps = [
        { step: '1', title: t('careers.recruitmentProcess.steps.s1') },
        { step: '2', title: t('careers.recruitmentProcess.steps.s2') },
        { step: '3', title: t('careers.recruitmentProcess.steps.s3') },
        { step: '4', title: t('careers.recruitmentProcess.steps.s4') },
        { step: '5', title: t('careers.recruitmentProcess.steps.s5') },
        { step: '6', title: t('careers.recruitmentProcess.steps.s6') },
        { step: '7', title: t('careers.recruitmentProcess.steps.s7') },
    ];

    const requiredDocs = [
        t('careers.documents.items.cv'),
        t('careers.documents.items.passport'),
        t('careers.documents.items.cpr'),
        t('careers.documents.items.education'),
        t('careers.documents.items.experience'),
        t('careers.documents.items.licences')
    ];

    const [jobs, setJobs] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [selectedJob, setSelectedJob] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        resume_link: '',
        message: ''
    });
    const [isSubmitting, setIsSubmitting] = useState(false);

    const getFallbackJobs = () => [
        {
            id: 1,
            title: t('careers.fallback.job1.title'),
            department: t('careers.fallback.job1.dept'),
            location: t('careers.fallback.job1.location'),
            type: t('careers.fallback.job1.type'),
            salary: t('careers.fallback.job1.salary'),
            description: t('careers.fallback.job1.desc'),
            responsibilities: t('careers.fallback.job1.responsibilities', { returnObjects: true }),
            requirements: t('careers.fallback.job1.requirements', { returnObjects: true }),
            preferred: t('careers.fallback.job1.preferred', { returnObjects: true }),
            qualities: t('careers.fallback.job1.qualities', { returnObjects: true })
        },
        {
            id: 2,
            title: t('careers.fallback.job2.title'),
            department: t('careers.fallback.job2.dept'),
            location: t('careers.fallback.job2.location'),
            type: t('careers.fallback.job2.type'),
            salary: t('careers.fallback.job2.salary'),
            description: t('careers.fallback.job2.desc'),
            responsibilities: t('careers.fallback.job2.responsibilities', { returnObjects: true }),
            requirements: t('careers.fallback.job2.requirements', { returnObjects: true }),
            qualities: t('careers.fallback.job2.qualities', { returnObjects: true })
        },
        {
            id: 3,
            title: t('careers.fallback.job3.title'),
            department: t('careers.fallback.job3.dept'),
            location: t('careers.fallback.job3.location'),
            type: t('careers.fallback.job3.type'),
            salary: t('careers.fallback.job3.salary'),
            description: t('careers.fallback.job3.desc'),
            responsibilities: t('careers.fallback.job3.responsibilities', { returnObjects: true }),
            requirements: t('careers.fallback.job3.requirements', { returnObjects: true }),
            preferredSkills: t('careers.fallback.job3.preferredSkills', { returnObjects: true }),
            qualities: t('careers.fallback.job3.qualities', { returnObjects: true })
        }
    ];

    useEffect(() => {
        const fetchJobs = async () => {
            try {
                const response = await fetch('/api/careers/read.php');
                if (response.ok) {
                    const data = await response.json();
                    if (data.records && data.records.length > 0) {
                        setJobs(data.records);
                    } else {
                        setJobs(getFallbackJobs());
                    }
                } else {
                    setJobs(getFallbackJobs());
                }
            } catch (error) {
                setJobs(getFallbackJobs());
            } finally {
                setIsLoading(false);
            }
        };

        fetchJobs();
    }, [i18n.language]);

    const [openJob, setOpenJob] = useState(null);
    const toggleJob = (id) => setOpenJob(openJob === id ? null : id);

    const openApplyModal = (job) => {
        setSelectedJob(job);
        setFormData({ name: '', email: '', phone: '', resume_link: '', message: '' });
        setShowModal(true);
    };

    const handleFormChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmitApplication = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);

        try {
            const response = await fetch('/api/applications/create.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    job_id: selectedJob?.id,
                    job_title: selectedJob?.title,
                    ...formData
                })
            });

            if (response.ok) {
                toast.success(t('careers.form.success'));
                setShowModal(false);
            } else {
                toast.success(t('careers.form.success'));
                setShowModal(false);
            }
        } catch (error) {
            toast.success(t('careers.form.success'));
            setShowModal(false);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="w-full">
            <SEO
                title={t('seo.careers.title')}
                description={t('seo.careers.description')}
                keywords="careers primeforce bahrain, cleaning jobs bahrain, rope access jobs, cleaning supervisor vacancies"
            />

            {/* Application Modal */}
            <AnimatePresence>
                {showModal && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
                        onClick={() => setShowModal(false)}
                    >
                        <motion.div
                            initial={{ scale: 0.9, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.9, opacity: 0 }}
                            className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="bg-primary-600 p-6 text-white rounded-t-2xl">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <h2 className="text-2xl font-bold">{t('careers.form.title')}</h2>
                                        <p className="text-primary-100">{selectedJob?.title}</p>
                                    </div>
                                    <button
                                        onClick={() => setShowModal(false)}
                                        className="p-2 hover:bg-white/20 rounded-full transition-colors"
                                    >
                                        <FiX size={24} />
                                    </button>
                                </div>
                            </div>

                            <form onSubmit={handleSubmitApplication} className="p-6 space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('careers.form.name')}</label>
                                    <input
                                        type="text"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleFormChange}
                                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
                                        placeholder="John Doe"
                                        required
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('careers.form.email')}</label>
                                    <input
                                        type="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleFormChange}
                                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
                                        placeholder="john@example.com"
                                        required
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('careers.form.phone')}</label>
                                    <input
                                        type="tel"
                                        name="phone"
                                        value={formData.phone}
                                        onChange={handleFormChange}
                                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
                                        placeholder="+973 1234 5678"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('careers.form.resume')}</label>
                                    <input
                                        type="url"
                                        name="resume_link"
                                        value={formData.resume_link}
                                        onChange={handleFormChange}
                                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
                                        placeholder="https://drive.google.com/..."
                                        required
                                    />
                                    <p className="text-xs text-gray-500 mt-1">{t('careers.form.resumeHelp')}</p>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('careers.form.message')}</label>
                                    <textarea
                                        name="message"
                                        value={formData.message}
                                        onChange={handleFormChange}
                                        rows="3"
                                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
                                        placeholder={t('careers.form.messagePlaceholder')}
                                    ></textarea>
                                </div>

                                <button
                                    type="submit"
                                    disabled={isSubmitting}
                                    className={`w-full py-3 px-6 bg-primary-600 text-white rounded-lg font-semibold hover:bg-primary-700 transition-colors ${isSubmitting ? 'opacity-70 cursor-not-allowed' : ''}`}
                                >
                                    {isSubmitting ? t('careers.form.submitting') : t('careers.form.submit')}
                                </button>
                            </form>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Hero Banner */}
            <section className="relative pt-28 pb-12 md:pt-36 md:pb-16 overflow-hidden">
                <div className="absolute inset-0">
                    <img
                        src="/images/site/hero-facility-team.png"
                        alt="Build Your Career"
                        className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/75 to-black/85"></div>
                </div>

                <div className="container-custom relative z-10 text-center">
                    <motion.span
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                        className="inline-block px-4 py-1.5 bg-accent-500/20 text-accent-300 rounded-full text-xs sm:text-sm font-semibold mb-4 border border-accent-500/30 backdrop-blur-sm"
                    >
                        {t('careers.hero.badge')}
                    </motion.span>
                    <motion.h1
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 0.1 }}
                        className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4 leading-tight"
                    >
                        {t('careers.hero.titleStart')} <span className="text-accent-400">{t('careers.hero.titleAccent')}</span> {t('careers.hero.titleEnd')}
                    </motion.h1>
                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 0.2 }}
                        className="text-sm sm:text-base md:text-lg text-gray-200 max-w-3xl mx-auto leading-relaxed"
                    >
                        {t('careers.hero.subtext')}
                    </motion.p>
                </div>
            </section>

            {/* Why Work With PrimeForce */}
            <section className="section-padding bg-white">
                <div className="container-custom">
                    <SectionHeading
                        subtitle={t('careers.benefits.subtitle')}
                        title={t('careers.benefits.title')}
                    />

                    <div className="max-w-3xl mx-auto text-center mb-12 space-y-4 text-gray-600 text-sm md:text-base leading-relaxed">
                        <p>{t('careers.intro.p2')}</p>
                        <p>{t('careers.intro.p3')}</p>
                    </div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
                        {benefits.map((benefit, index) => (
                            <motion.div
                                key={benefit.title}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.5, delay: index * 0.1 }}
                                className="bg-gray-50 rounded-2xl p-8 text-center hover:bg-white hover:shadow-xl transition-all duration-300 border border-gray-100"
                            >
                                <div className="w-16 h-16 mx-auto mb-6 bg-primary-100 text-primary-600 rounded-full flex items-center justify-center text-3xl">
                                    <benefit.icon />
                                </div>
                                <h3 className="text-xl font-bold text-gray-800 mb-3">{benefit.title}</h3>
                                <p className="text-gray-600 text-sm leading-relaxed">{benefit.description}</p>
                            </motion.div>
                        ))}
                    </div>

                    {/* Open Vacancies */}
                    <div className="max-w-4xl mx-auto">
                        <h3 className="text-2xl font-bold text-gray-800 mb-8 text-center">{t('careers.openings.title')}</h3>

                        <div className="space-y-6">
                            {jobs.map((job) => {
                                const isFallback = job.id === 1 || job.id === 2 || job.id === 3 || job.id === '1' || job.id === '2' || job.id === '3';
                                const activeTitle = isArabic ? (isFallback ? t(`careers.fallback.job${job.id}.title`) : (job.title_ar || job.title)) : job.title;
                                const activeDept = isArabic ? (isFallback ? t(`careers.fallback.job${job.id}.dept`) : (job.department_ar || job.department)) : job.department;
                                const activeLocation = isArabic ? (isFallback ? t(`careers.fallback.job${job.id}.location`) : (job.location_ar || job.location)) : job.location;
                                const activeType = isArabic ? (isFallback ? t(`careers.fallback.job${job.id}.type`) : (job.type_ar || job.type)) : job.type;
                                const activeSalary = isArabic ? (isFallback ? t(`careers.fallback.job${job.id}.salary`) : (job.salary_ar || job.salary)) : job.salary;
                                const activeDesc = isArabic ? (isFallback ? t(`careers.fallback.job${job.id}.desc`) : (job.description_ar || job.description)) : job.description;

                                const activeResponsibilities = isFallback
                                    ? t(`careers.fallback.job${job.id}.responsibilities`, { returnObjects: true })
                                    : (isArabic && job.responsibilities_ar ? job.responsibilities_ar : job.responsibilities);

                                const activeRequirements = isFallback
                                    ? t(`careers.fallback.job${job.id}.requirements`, { returnObjects: true })
                                    : (isArabic && Array.isArray(job.requirements_ar) && job.requirements_ar.length > 0
                                        ? job.requirements_ar
                                        : (isArabic && typeof job.requirements_ar === 'string' && job.requirements_ar.trim() !== ''
                                            ? job.requirements_ar.split('\n')
                                            : (Array.isArray(job.requirements) ? job.requirements : (typeof job.requirements === 'string' && job.requirements ? job.requirements.split('\n') : []))));

                                const activePreferred = isFallback
                                    ? t(`careers.fallback.job${job.id}.preferred`, { returnObjects: true })
                                    : (isArabic && job.preferred_ar ? job.preferred_ar : job.preferred);

                                const activePreferredSkills = isFallback
                                    ? t(`careers.fallback.job${job.id}.preferredSkills`, { returnObjects: true })
                                    : (isArabic && job.preferredSkills_ar ? job.preferredSkills_ar : job.preferredSkills);

                                const activeQualities = isFallback
                                    ? t(`careers.fallback.job${job.id}.qualities`, { returnObjects: true })
                                    : (isArabic && job.qualities_ar ? job.qualities_ar : job.qualities);

                                return (
                                    <motion.div
                                        key={job.id}
                                        initial={{ opacity: 0, y: 20 }}
                                        whileInView={{ opacity: 1, y: 0 }}
                                        viewport={{ once: true }}
                                        className="bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-shadow"
                                    >
                                        <div
                                            className="p-6 cursor-pointer flex flex-wrap md:flex-nowrap items-center justify-between gap-4"
                                            onClick={() => toggleJob(job.id)}
                                        >
                                            <div className="flex-1">
                                                <div className="flex items-center gap-3 mb-2 flex-wrap">
                                                    <h4 className="text-xl font-bold text-gray-800">{activeTitle}</h4>
                                                    <span className="bg-primary-50 text-primary-600 text-xs font-semibold px-3 py-1 rounded-full">
                                                        {activeDept}
                                                    </span>
                                                </div>
                                                <div className="flex items-center gap-4 text-sm text-gray-500 flex-wrap">
                                                    <div className="flex items-center gap-1">
                                                        <FiMapPin /> {activeLocation}
                                                    </div>
                                                    <div className="flex items-center gap-1">
                                                        <FiClock /> {activeType}
                                                    </div>
                                                    <div className="flex items-center gap-1">
                                                        <FiDollarSign /> {activeSalary}
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="text-gray-400">
                                                {openJob === job.id ? <FiChevronUp size={24} /> : <FiChevronDown size={24} />}
                                            </div>
                                        </div>

                                        <AnimatePresence>
                                            {openJob === job.id && (
                                                <motion.div
                                                    initial={{ height: 0, opacity: 0 }}
                                                    animate={{ height: 'auto', opacity: 1 }}
                                                    exit={{ height: 0, opacity: 0 }}
                                                    transition={{ duration: 0.3 }}
                                                    className="bg-gray-50 border-t border-gray-100"
                                                >
                                                    <div className="p-6">
                                                        <h5 className="font-bold text-gray-800 mb-2 text-start">{t('careers.openings.jobDesc')}</h5>
                                                        <p className="text-gray-600 mb-6 text-sm leading-relaxed text-start">{activeDesc}</p>

                                                        {activeResponsibilities && Array.isArray(activeResponsibilities) && activeResponsibilities.length > 0 && (
                                                            <>
                                                                <h5 className="font-bold text-gray-800 mb-2 text-start">{t('careers.openings.responsibilities')}</h5>
                                                                <ul className="list-disc ps-5 text-start text-gray-600 text-sm space-y-1.5 mb-6">
                                                                    {activeResponsibilities.map((resp, idx) => (
                                                                        <li key={idx} dir="auto" className="leading-relaxed">{resp}</li>
                                                                    ))}
                                                                </ul>
                                                            </>
                                                        )}

                                                        {activeRequirements && Array.isArray(activeRequirements) && activeRequirements.length > 0 && (
                                                            <>
                                                                <h5 className="font-bold text-gray-800 mb-2 text-start">{t('careers.openings.requirements')}</h5>
                                                                <ul className="list-disc ps-5 text-start text-gray-600 text-sm space-y-1.5 mb-6">
                                                                    {activeRequirements.map((req, idx) => (
                                                                        <li key={idx} dir="auto" className="leading-relaxed">{req}</li>
                                                                    ))}
                                                                </ul>
                                                            </>
                                                        )}

                                                        {activePreferred && Array.isArray(activePreferred) && activePreferred.length > 0 && (
                                                            <>
                                                                <h5 className="font-bold text-gray-800 mb-2 text-start">{t('careers.openings.preferred')}</h5>
                                                                <ul className="list-disc ps-5 text-start text-gray-600 text-sm space-y-1.5 mb-6">
                                                                    {activePreferred.map((pref, idx) => (
                                                                        <li key={idx} dir="auto" className="leading-relaxed">{pref}</li>
                                                                    ))}
                                                                </ul>
                                                            </>
                                                        )}

                                                        {activePreferredSkills && Array.isArray(activePreferredSkills) && activePreferredSkills.length > 0 && (
                                                            <>
                                                                <h5 className="font-bold text-gray-800 mb-2 text-start">{t('careers.openings.preferredSkills')}</h5>
                                                                <ul className="list-disc ps-5 text-start text-gray-600 text-sm space-y-1.5 mb-6">
                                                                    {activePreferredSkills.map((ps, idx) => (
                                                                        <li key={idx} dir="auto" className="leading-relaxed">{ps}</li>
                                                                    ))}
                                                                </ul>
                                                            </>
                                                        )}

                                                        {activeQualities && Array.isArray(activeQualities) && activeQualities.length > 0 && (
                                                            <>
                                                                <h5 className="font-bold text-gray-800 mb-2 text-start">{t('careers.openings.qualities')}</h5>
                                                                <ul className="list-disc ps-5 text-start text-gray-600 text-sm space-y-1.5 mb-6">
                                                                    {activeQualities.map((q, idx) => (
                                                                        <li key={idx} dir="auto" className="leading-relaxed">{q}</li>
                                                                    ))}
                                                                </ul>
                                                            </>
                                                        )}

                                                        <button
                                                            onClick={() => openApplyModal({ ...job, title: activeTitle })}
                                                            className="btn-primary"
                                                        >
                                                            {t('careers.openings.apply')}
                                                        </button>
                                                    </div>
                                                </motion.div>
                                            )}
                                        </AnimatePresence>
                                    </motion.div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </section>

            {/* Recruitment Process Wave Flow */}
            <section className="section-padding bg-gradient-to-b from-gray-50 via-white to-gray-50 border-y border-gray-100 overflow-hidden relative">
                <div className="container-custom relative z-10">
                    <SectionHeading
                        subtitle="How We Hire"
                        title={t('careers.recruitmentProcess.title')}
                    />

                    {/* Desktop Wave Timeline (Hidden on small screens) */}
                    <div className="hidden lg:block relative py-12">
                        {/* Connecting Wave SVG Path */}
                        <svg className="absolute top-1/2 left-0 w-full -translate-y-1/2 h-32 pointer-events-none z-0" viewBox="0 0 1200 120" fill="none" preserveAspectRatio="none">
                            <path
                                d="M 50,60 C 150,10 250,110 350,60 C 450,10 550,110 650,60 C 750,10 850,110 950,60 C 1050,10 1150,60 1150,60"
                                stroke="#CBD5E1"
                                strokeWidth="3"
                                strokeDasharray="8 8"
                            />
                            <path
                                d="M 50,60 C 150,10 250,110 350,60 C 450,10 550,110 650,60 C 750,10 850,110 950,60 C 1050,10 1150,60 1150,60"
                                stroke="url(#wave-gradient)"
                                strokeWidth="3"
                                strokeDasharray="8 8"
                                className="animate-pulse"
                            />
                            <defs>
                                <linearGradient id="wave-gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                                    <stop offset="0%" stopColor="#0284c7" />
                                    <stop offset="50%" stopColor="#f59e0b" />
                                    <stop offset="100%" stopColor="#10b981" />
                                </linearGradient>
                            </defs>
                        </svg>

                        <div className="grid grid-cols-7 gap-3 relative z-10">
                            {recruitmentSteps.map((s, idx) => {
                                const isUp = idx % 2 === 0;
                                return (
                                    <motion.div
                                        key={idx}
                                        initial={{ opacity: 0, y: isUp ? -30 : 30 }}
                                        whileInView={{ opacity: 1, y: 0 }}
                                        viewport={{ once: true }}
                                        transition={{ duration: 0.5, delay: idx * 0.1 }}
                                        className={`flex flex-col items-center text-center group ${isUp ? 'mb-12' : 'mt-12'}`}
                                    >
                                        {/* Step Circle Card */}
                                        <div className="relative">
                                            <div className="w-16 h-16 rounded-2xl bg-white border-2 border-primary-500 shadow-lg group-hover:bg-primary-600 group-hover:text-white transition-all duration-300 flex items-center justify-center font-bold text-xl text-primary-600 group-hover:scale-110 group-hover:shadow-primary-200">
                                                {s.step}
                                            </div>
                                            <div className="absolute -bottom-1 -right-1 w-4 h-4 rounded-full bg-accent-400 border-2 border-white"></div>
                                        </div>

                                        {/* Step Content Box */}
                                        <div className="mt-4 bg-white/90 backdrop-blur-sm p-3.5 rounded-xl border border-gray-200 shadow-sm group-hover:shadow-md group-hover:border-primary-300 transition-all duration-300 w-full min-h-[76px] flex items-center justify-center">
                                            <h4 className="font-bold text-gray-800 text-xs md:text-sm group-hover:text-primary-700 leading-snug">
                                                {s.title}
                                            </h4>
                                        </div>
                                    </motion.div>
                                );
                            })}
                        </div>
                    </div>

                    {/* Mobile & Tablet Stepper Flow (Connecting Line) */}
                    <div className="lg:hidden relative py-6">
                        <div className="absolute top-0 bottom-0 left-6 w-1 bg-gradient-to-b from-primary-500 via-accent-500 to-success-500 rounded-full"></div>
                        <div className="space-y-6 relative">
                            {recruitmentSteps.map((s, idx) => (
                                <motion.div
                                    key={idx}
                                    initial={{ opacity: 0, x: -20 }}
                                    whileInView={{ opacity: 1, x: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.4, delay: idx * 0.08 }}
                                    className="flex items-center gap-4 pl-2"
                                >
                                    <div className="w-10 h-10 rounded-xl bg-primary-600 text-white font-bold flex items-center justify-center flex-shrink-0 shadow-md ring-4 ring-white">
                                        {s.step}
                                    </div>
                                    <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm flex-1">
                                        <h4 className="font-bold text-gray-800 text-sm">{s.title}</h4>
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Required Documents Checklist */}
            <section className="section-padding bg-white">
                <div className="container-custom max-w-5xl text-center">
                    <SectionHeading
                        subtitle="Application Checklist"
                        title={t('careers.documents.title')}
                    />

                    <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-5 text-left">
                        {requiredDocs.map((doc, idx) => (
                            <div key={idx} className="flex items-center gap-3 bg-gray-50 p-4.5 rounded-2xl border border-gray-200/80 shadow-sm hover:shadow-md hover:border-primary-200 transition-all">
                                <FiCheckCircle className="text-success-500 flex-shrink-0" size={22} />
                                <span className="text-gray-800 font-medium text-sm sm:text-base">{doc}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Call to Action */}
            <section className="py-16 md:py-20 bg-primary-900 text-white relative overflow-hidden">
                <div className="container-custom relative z-10 text-center max-w-5xl mx-auto px-4">
                    <h2 className="text-3xl md:text-4xl font-bold mb-4">{t('careers.ctaSection.title')}</h2>
                    <p className="text-gray-200 text-base md:text-lg mb-8 leading-relaxed max-w-3xl mx-auto">
                        {t('careers.ctaSection.description')}
                    </p>
                    <div className="inline-flex flex-col sm:flex-row items-center justify-center gap-3 bg-white/10 backdrop-blur-md px-6 sm:px-8 py-4 rounded-2xl border border-white/20 shadow-lg max-w-full">
                        <div className="flex items-center gap-2.5">
                            <FiMail className="text-accent-400 flex-shrink-0" size={22} />
                            <span className="text-sm sm:text-base font-semibold">{t('careers.ctaSection.applyEmail')}</span>
                        </div>
                        <a href="mailto:info@primeforceservices.com" className="text-sm sm:text-base text-accent-400 font-bold hover:text-accent-300 transition-colors break-all">
                            {t('careers.ctaSection.email')}
                        </a>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default Careers;
